import { useState } from 'react';

import Button from 'components/Button';
import Icon from 'components/Icon';
import ScreenReaderAnnouncementText from 'components/ScreenReaderAnnouncementText';
import { cn } from 'utils/classNames';
import { ignorePromiseRejection, is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

interface Props {
	className?: string;
	feedbackDoneText: string;
	feedbackUrl: string;
	reportButtonText: string;
	upvoteButtonText: string;
	votesUp: number;
}

/** Review feedback to Trustvoice */
export default function ReviewFeedback({
	className,
	feedbackDoneText,
	feedbackUrl,
	reportButtonText,
	upvoteButtonText,
	votesUp: votesUpProp,
}: Props) {
	const { t } = useI18n();
	const [votesUp, setVotesUp] = useState(votesUpProp);
	const [feedbackStatus, setFeedbackStatus] = useState<
		'bad' | 'done' | 'up' | null
	>(null);

	const feedbackAction = async (feedback: 'bad' | 'up') => {
		if (feedbackStatus === feedback) {
			return;
		}
		setFeedbackStatus(feedback);
		try {
			// https://developer.testfreaks.com/docs/json-api-collect
			await fetch(feedbackUrl, {
				method: 'POST',
				headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
				body: new URLSearchParams({ type: feedback }),
			});
		} catch {
			// Don't bother with any error handling, pretend it's always successful.
			// Among the tiny amount of users that actually use this, it's probably
			// a tiny fraction that would care enough to retry in case of error.
		}
		setFeedbackStatus('done');
		// Make it appear as if upvotes are immediately updated.
		if (feedback === 'up') {
			setVotesUp((v) => v + 1);
		}
	};

	const handleUpvoteClick = () => {
		ignorePromiseRejection(feedbackAction('up'));
	};
	const handleReportClick = () => {
		ignorePromiseRejection(feedbackAction('bad'));
	};

	const isLeavingFeedback = is.oneOf(feedbackStatus, 'up', 'bad');
	const hasLeftFeedback = feedbackStatus === 'done';
	const disabled = isLeavingFeedback || hasLeftFeedback;

	return (
		<div className={cn('flex flex-wrap items-center gap-x-6', className)}>
			<Button
				variant="text"
				className="group/upvote"
				underline={false}
				hasExpandedHitArea
				aria-label={`${upvoteButtonText} (${votesUp})`}
				onClick={handleUpvoteClick}
				disabled={disabled}
			>
				<Icon
					name={feedbackStatus === 'up' ? 'loading' : 'thumb-up'}
					size={16}
				/>
				<span className="ml-1 font-bold">{votesUp}</span>
				<span
					className={cn(
						'ml-2 underline',
						!disabled && 'group-hover/upvote:underline-strong',
					)}
				>
					{upvoteButtonText}
				</span>
			</Button>

			<Button
				variant="text"
				hasExpandedHitArea
				onClick={handleReportClick}
				disabled={disabled}
			>
				{reportButtonText}
				{feedbackStatus === 'bad' && (
					<Icon name="loading" size={16} className="ml-1" />
				)}
			</Button>

			<ScreenReaderAnnouncementText
				as="span"
				text={
					isLeavingFeedback
						? t('general_loading_text')
						: hasLeftFeedback
							? feedbackDoneText
							: ''
				}
				className={cn('text-success', isLeavingFeedback && 'sr-only')}
			/>
		</div>
	);
}
ReviewFeedback.displayName = 'ReviewFeedback';
